import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/styles";
import { PageHeaderTabs } from "features/health-safety-forms";
import { getPageResults, useIsOnline, isStaffUser, useRouter } from "utils";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	Pagination,
	PaginationHeader,
	PaginationLoader,
	PopupSnackbar,
	PageHeaderListActions,
} from "frame/components";
import { connect, useDispatch, useSelector } from "react-redux";
import {
	ADD_LEAVE_ROUTE,
	ADD_ADMIN_LEAVE_ROUTE,
	NEW_LEAVE_PATH,
	LIST_LEAVES_ROUTE
} from "../leave-routes";
import { EditTimesheetForm } from "../../timesheet/components/edit-timesheet-form";
import { LeaveCardCollapsed } from "./leave-card-collapsed";
import { TASK_ROUTE } from "features/health-safety-forms";
import { onList as onListLeaveTypes } from "../../leave-type/actions";
import {
	Grid,
	TextField,
	InputAdornment,
	Typography,
	Card,
	CardContent,
	IconButton,
	Tooltip
} from "@material-ui/core";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialTimesheetState } from "../../timesheet/timesheet-reducer";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import moment from "moment";
import { SITES_ROUTE } from "../../sites/actions/types";
import InfiniteScroll from "react-infinite-scroll-component";
import { getPagedLeaveRecords, deleteLeaveRecord } from "../leave-actions";

const useStyles = makeStyles((theme) => ({
	filter: {
		"position": "relative",
		"right": 25,
		"top": 10,
		"marginTop": theme.spacing(0.1),
		"& .MuiOutlinedInput-input": {
			padding: theme.spacing(1.7, 2)
		},
		"& .MuiInputLabel-outlined": {
			transform: "translate(14px, 14px) scale(1)"
		},
		"& .MuiInputLabel-shrink": {
			transform: "translate(14px, -6px) scale(0.75)"
		},
		[theme.breakpoints.down("sm")]: {
			right: -100
		},
		[theme.breakpoints.down("xs")]: {
			top: 50,
			left: 20,
			minWidth: "100px"
		},
		"minWidth": "150px"
	},
	search: {
		display: "flex"
	},
	button: {
		"padding": theme.spacing(1.75, 3),
		"color": theme.palette.brand,
		"fontWeight": 600,
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 3)
		},
		"background": theme.palette.highlight,
		"borderColor": theme.palette.highlight,
		"&:hover": {
			background: "#c1c72c",
			borderColor: "#c1c72c"
		},
		"width": "100%"
	},
	buttonSecondary: {
		"padding": theme.spacing(1.75, 3),
		"color": theme.palette.text.secondary,
		"fontWeight": 600,
		"&:hover": {
			color: theme.palette.brand
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(1, 3)
		},
		"width": "100%"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
	filtersChip: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.white
	},
	chipsContainer: {
		marginTop: theme.spacing(1)
	},
	chipLabel: {
		color: theme.palette.text.tertiary
	},
	root: {
		minHeight: "600px",
		overflow: "visible"
	},
	content: {
		minHeight: "500px",
		overflow: "visible"
	},
	dateGroup: {
		marginBottom: theme.spacing(3)
	},
	dateHeader: {
		fontSize: theme.typography.body2.fontSize,
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
		[theme.breakpoints.up("sm")]: {
			fontSize: theme.typography.subtitle1.fontSize,
			color: theme.palette.text.primary,
		},
	},
}));

const mapStateToProps = ({ profile, timesheetFormReducer, organisations }) => ({
	timesheetForm: timesheetFormReducer,
	role: profile.role,
	currentStaffId: profile.staffId,
	currentOrganisationGUID: organisations.currentOrganisation
});

const mapDispatchToProps = {};

export const LeaveList = connect(
	mapStateToProps,
	mapDispatchToProps
)(({ role, timesheetForm, currentOrganisationGUID }) => {
	const classes = useStyles();
	const { match } = useRouter();
	const { siteId } = match.params;
	const dispatch = useDispatch();
	
	// Move useSelector hook outside of the conditional to avoid hooks dependency errors
	const siteList = useSelector(({ sites }) => sites.pagination?.results);
	
	let siteName = "";
	if (siteId && siteList) {
		siteName = siteList.find((site) => site.id == siteId)?.siteName || "";
	}
	
	// Remove "leave" from the initial search since we now have a dedicated endpoint
	const [searchFilter, setSearchFilter] = useState(siteName || "");
	// Removed status filter
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const [leaveList, setLeaveList] = useState(getPageResults(initialTimesheetState));
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [showFilters, setShowFilters] = useState(false); // Collapsed by default
	const isInitialMount = useRef(true);
	const hasActiveTimesheet = timesheetForm.timesheet.toggle;
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreLeaves, setHasMoreLeaves] = useState(true);
	const defaultPageSize = 20;
	const [snackMessage, setSnackMessage] = useState(null);
	const [useCollapsedView, setUseCollapsedView] = useState(true);

	// Group leaves by date
	const groupLeavesByDate = (leaves) => {
		const grouped = {};
		const today = moment().startOf('day');
		const currentYear = moment().year();

		const formatDateLabel = (date) => {
			if (date.isSame(today, 'day')) {
				return 'Today';
			}
			const base = date.format('dddd D MMM');
			return date.year() === currentYear ? base : `${base}, ${date.year()}`;
		};

		leaves.forEach(leave => {
			// Convert from UTC to local time before grouping so headers reflect local date
			const leaveDate = moment.utc(leave.startDateTimeUTC).local().startOf('day');
			const dateKey = leaveDate.format('YYYY-MM-DD');
			const dateLabel = formatDateLabel(leaveDate);
			
			if (!grouped[dateKey]) {
				grouped[dateKey] = {
					label: dateLabel,
					leaves: []
				};
			}
			grouped[dateKey].leaves.push(leave);
		});
		
		// Sort by date (most recent first)
		return Object.keys(grouped)
			.sort((a, b) => moment(b).diff(moment(a)))
			.map(dateKey => ({
				dateKey,
				...grouped[dateKey]
			}));
	};

	useEffect(() => {
		console.log("Leave List component mounted");
		// Ensure leave types are loaded so the list can render the correct leave type label by ID.
		dispatch(onListLeaveTypes());
		getLeaves();
	}, [dispatch]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setLeaveList([]);
			setTotalNumberOfRecords(1);
			setHasMoreLeaves(true);
			setIsDoneFetching(false);
			getLeaves(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	// function to setNewLeaveState only called from getLeaves function
	const setNewLeaveState = (leaveResult, page) => {
		let newState = initialTimesheetState;

		if (leaveResult && leaveResult.results && leaveResult.results.length > 0) {
			newState = {
				...newState,
				...leaveResult
			};
		}

		newState.refreshList = leaveResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the results otherwise add to the end of it
		if (page === 1) {
			setLeaveList([...getPageResults(newState)]);
		} else {
			setLeaveList([...leaveList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getLeaves = (
		fromPageToRequest = currentPage,
		recordDeletedOrUpdated = false,
		pageSize = defaultPageSize
	) => {
		console.log("Getting leaves with filter:", searchFilter);
		
		if (totalNumberOfRecords !== leaveList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreLeaves(true);
			}

			// Use the dedicated leaves/paged endpoint
			getPagedLeaveRecords(currentOrganisationGUID, {
				ascending: false,
				orderBy: "isActive,startDateTimeUTC",
				page: fromPageToRequest,
				pageSize,
				searchKey: searchFilter
			})
			.then((result) => {
				console.log("Leave API response:", result.data);
				
				setNewLeaveState(result.data, fromPageToRequest);

				// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
				if (pageSize > defaultPageSize && fromPageToRequest === 1) {
					setCurrentPage(pageSize / defaultPageSize);
				} else {
					setCurrentPage(fromPageToRequest);
				}

				setTotalNumberOfRecords(result.data.totalNumberOfRecords);
				if (
					result.data.totalNumberOfRecords ==
					leaveList.length + result.data.results.length
				) {
					setHasMoreLeaves(false);
				}
			})
			.catch(error => {
				console.error("Error fetching leaves:", error);
				// Add error handling with snackbar
				setSnackMessage && setSnackMessage({
					message: "Failed to load leave records",
					severity: "error"
				});
				setIsDoneFetching(true);
			});
		} else {
			setHasMoreLeaves(false);
		}
	};

	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	// The InfiniteScroll Component requires a height for it to work effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			if (ref.current) {
				setHeight(ref.current.offsetHeight - 20); // reduce height by 20px otherwise the scrollbar flows past the height of screen.
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return (
		<Page className={classes.root} title="Leaves" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				<InfiniteScroll
					dataLength={leaveList.length}
					next={() => {
						getLeaves(currentPage + 1);
					}}
					hasMore={hasMoreLeaves}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />
											<PageHeaderTabs
							title="Leave"
							staffUserHide={false}
							addTabTitle="New"
							addTabPath={NEW_LEAVE_PATH}
							listTabPath={LIST_LEAVES_ROUTE}
							hideListTabButton={true}
							addAdminPath={ADD_ADMIN_LEAVE_ROUTE}
							showAddAdminButton={false}
							showLeaveButton={false}
							newLeavePath={NEW_LEAVE_PATH}
							backActionPath={null}
						
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
							showCollapseToggle
							useCollapsedView={useCollapsedView}
							onToggleCollapsedView={() => setUseCollapsedView(!useCollapsedView)}
						/>
					}>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchFilter("");
																	setCurrentPage(1);
																	getLeaves(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={"Search by leave type, staff name, or notes"}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderTabs>
					<PageContent pad>
						<p style={{ marginTop: 5 }}>
							{leaveList.length > 0 ? totalNumberOfRecords + " records" : "No leave records loaded"}
						</p>
						<div>
							{leaveList.length > 0 && isDoneFetching && (
								useCollapsedView ? (
									// Collapsed grouped view
									groupLeavesByDate(leaveList).map((dateGroup) => (
										<div key={dateGroup.dateKey} className={classes.dateGroup}>
											<Typography className={classes.dateHeader}>
												{dateGroup.label}
											</Typography>
											{dateGroup.leaves.map((leave, index) => (
												<LeaveCardCollapsed
													key={`${dateGroup.dateKey}-${index}`}
													leave={leave}
													onEdit={onEdit(leave.id)}
													onDelete={(id) => {
														deleteLeaveRecord(currentOrganisationGUID, id)
															.then(() => {
																setSnackMessage({
																	message: "Leave deleted successfully",
																	severity: "success"
																});
																getLeaves(1, true);
															})
															.catch(error => {
																console.error("Error deleting leave:", error);
																setSnackMessage({
																	message: "Failed to delete leave record",
																	severity: "error"
																});
															});
													}}
													role={role}
												/>
											))}
										</div>
									))
									) : (
										// Expand-all view using the same collapsed cards
										groupLeavesByDate(leaveList).map((dateGroup) => (
											<div key={dateGroup.dateKey} className={classes.dateGroup}>
												<Typography className={classes.dateHeader}>
													{dateGroup.label}
												</Typography>
												{dateGroup.leaves.map((leave, index) => (
													<LeaveCardCollapsed
														key={`${dateGroup.dateKey}-${index}`}
														leave={leave}
														onEdit={onEdit(leave.id)}
														onDelete={(id) => {
															deleteLeaveRecord(currentOrganisationGUID, id)
																.then(() => {
																	setSnackMessage({
																		message: "Leave deleted successfully",
																		severity: "success"
																	});
																	getLeaves(1, true);
																})
																.catch(error => {
																	console.error("Error deleting leave:", error);
																	setSnackMessage({
																		message: "Failed to delete leave record",
																		severity: "error"
																	});
																});
														}}
														role={role}
														forceExpanded={true}
													/>
												))}
											</div>
										))
									)
							)}
						</div>
					</PageContent>
					<EditTimesheetForm
						open={showEditModal}
						id={selectedItemId}
						timesheetList={leaveList}
						currentOrganisationGUID={currentOrganisationGUID}
						isLeaveRecord={true}
						onClose={() => {
							const leaveIndex = leaveList.findIndex((t) => t.id === selectedItemId);
							//leaveIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
							const pageSizeToLoad =
								Math.ceil((leaveIndex + 1) / defaultPageSize) * defaultPageSize;

							// Get leave first page to the place the user was at before editing (using pageSizetoLoad),
							getLeaves(1, true, pageSizeToLoad);

							setShowEditModal(false);
						}}
					/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreLeaves && isDoneFetching && <CardText message={"All records have been loaded."} />}
					{leaveList.length === 0 && isDoneFetching && (
						<CardText message={"No leave records to show."} />
					)}
				</InfiniteScroll>
				<PopupSnackbar 
					message={snackMessage?.message || timesheetForm.errorMessage} 
					severity={snackMessage?.severity || (timesheetForm.errorMessage ? "error" : undefined)}
					coloured={snackMessage?.severity === "error" || !!timesheetForm.errorMessage} 
					error={snackMessage?.severity === "error" || !!timesheetForm.errorMessage} 
					onExited={() => setSnackMessage(null)} 
				/>
			</div>
		</Page>
	);
});

export const CardText = ({ message, style }) => {
	const theme = useTheme();
	return (
		<Card
			style={{
				display: "flex",
				alignItems: "center",
				flexWrap: "wrap",
				position: "relative",
				margin: "0 24px 24px 24px",
				zIndex: 1,
				...style
			}}
		>
			<CardContent
				style={{
					padding: "16px",
					flexGrow: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%"
				}}
			>
				<p>{message}</p>
			</CardContent>
		</Card>
	);
}; 